import { useEffect, useState } from "react";
import { client } from "../net/client";
import { fa, faNum } from "../game/cards";
import { games } from "./games";

/**
 * لیگ، جام و رویداد — از نگاهِ بازیکن.
 *
 * تا امروز اینها فقط در پنلِ ادمین بودند و بازیکن هیچ‌جا نمی‌دیدشان؛ لیگی که
 * کسی از آن خبر ندارد لیگ نیست. این صفحه همان چیزی را نشان می‌دهد که سرور
 * عمومی می‌کند (`GET /v1/campaigns`) و نه بیشتر: کارزارِ پیش‌نویس یا
 * تمام‌شده اصلاً نمی‌آید.
 *
 * جدول حساب نمی‌خواهد — هر کسی می‌تواند ببیند چه خبر است. ولی «تو کجای
 * جدولی» با توکن می‌آید، چون رتبه‌ی هر کس مالِ خودِ اوست.
 */
type Campaign = {
  id: string; kind: "league" | "cup" | "event"; title: string; description: string; game: string;
  banner: string; starts_at_ms: number; ends_at_ms: number; config: Record<string, unknown>; entries: number;
};
type Row = { rank: number; name: string; points: number; played: number; wins: number; me: boolean; eliminated: boolean };
type Detail = { campaign: Campaign; board: Row[]; me: { rank: number; points: number; played: number; wins: number } | null };

/** بنرِ خراب یا نرسیده نباید جای خالیِ شکسته بگذارد. */
const hideImg = (e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = "none"; };

const KIND = { league: { fa: "لیگ", icon: "🏆" }, cup: { fa: "جام", icon: "🏅" }, event: { fa: "رویداد", icon: "🎉" } };
const gameName = (id: string) => games.find((g) => g.id === id)?.fa ?? (id ? id : "همه‌ی بازی‌ها");

/** «۳ روز مانده» — چیزی که بازیکن واقعاً می‌خواهد بداند. */
function left(endMs: number): string {
  const ms = endMs - Date.now();
  if (ms <= 0) return "تمام شد";
  const h = Math.floor(ms / 3600000);
  if (h < 1) return "کمتر از یک ساعت مانده";
  if (h < 48) return faNum(h) + " ساعت مانده";
  return faNum(Math.floor(h / 24)) + " روز مانده";
}

export function Cups({ onBack, initial }: { onBack: () => void; initial?: string | null }) {
  const [list, setList] = useState<Campaign[] | null>(null);
  // initial: کارزاری که لینکِ لندینگ خواسته — مستقیم جدولش باز می‌شود.
  const [open, setOpen] = useState<string | null>(initial ?? null);
  const [err, setErr] = useState("");
  useEffect(() => {
    client.rest<{ campaigns: Campaign[] }>("GET", "/v1/campaigns")
      .then((r) => setList(r.campaigns ?? []), () => setErr("به سرور نرسید"));
  }, []);

  if (open) return <Board id={open} onBack={() => setOpen(null)} />;
  return (
    <div className="page stack">
      <div className="row">
        <button className="hb" onClick={onBack}>← خانه</button>
        <b style={{ flex: 1 }}>لیگ و جام</b>
      </div>
      {err && <div className="muted">{err}</div>}
      {!err && list === null && <div className="muted">…</div>}
      {list?.length === 0 && (
        <div className="plate stack">
          <b>الان لیگ یا جامی برپا نیست</b>
          <div className="muted">هر وقت راه بیفتد همین‌جا، روی صفحه‌ی اصلیِ سایت، و در صندوقِ پیام‌ها خبرش می‌آید.</div>
        </div>
      )}
      {list?.map((c) => (
        <button key={c.id} className="cuprow" onClick={() => setOpen(c.id)}>
          <span className={"cupshot" + (c.banner ? "" : " noimg")} data-glyph={KIND[c.kind].icon}>
            {c.banner && <img className="cupbanner" src={c.banner} alt="" loading="lazy" onError={hideImg} />}
            <span className="cupkind">{KIND[c.kind].icon} {KIND[c.kind].fa}</span>
          </span>
          <span className="cupbody">
            <b>{c.title}</b>
            {c.description && <span className="cupdesc">{c.description}</span>}
            <span className="cupchips">
              <span>{gameName(c.game)}</span>
              <span>{left(c.ends_at_ms)}</span>
              {typeof c.config?.prize_coins === "number" && <span className="prize">جایزه {faNum(c.config.prize_coins as number)} سکه</span>}
              {c.kind !== "league" && c.entries > 0 && <span>{fa(c.entries)} نفر</span>}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}

function Board({ id, onBack }: { id: string; onBack: () => void }) {
  const [d, setD] = useState<Detail | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    client.rest<Detail>("GET", "/v1/campaigns/" + encodeURIComponent(id))
      .then(setD, () => setErr("این کارزار دیگر نیست"));
  }, [id]);

  if (err) return <div className="page stack"><button className="hb" onClick={onBack}>← لیگ و جام</button><div className="muted">{err}</div></div>;
  if (!d) return <div className="page stack"><button className="hb" onClick={onBack}>← لیگ و جام</button><div className="muted">…</div></div>;
  const c = d.campaign;
  const prize = c.config?.prize_coins;
  return (
    <div className="page stack">
      <div className="row">
        <button className="hb" onClick={onBack}>← لیگ و جام</button>
        <b style={{ flex: 1 }}>{KIND[c.kind].icon} {c.title}</b>
      </div>
      <div className="plate stack">
        {c.banner && <img className="cupbanner" src={c.banner} alt="" onError={hideImg} />}
        {c.description && <div>{c.description}</div>}
        <div className="row" style={{ gap: 10 }}>
          <span className="muted">{gameName(c.game)}</span>
          <span className="muted">{left(c.ends_at_ms)}</span>
          {typeof prize === "number" && <span className="muted">جایزه {faNum(prize)} سکه</span>}
        </div>
        {c.kind === "league" && <div className="muted">هر بازیِ آنلاین یک امتیاز دارد و هر برد سه امتیازِ دیگر. ثبت‌نام نمی‌خواهد — بازی کن، در جدول می‌آیی.</div>}
      </div>

      {d.me && (
        <div className="plate stack" style={{ borderColor: "var(--me-gold)" }}>
          <b>جای تو در جدول</b>
          <div className="row" style={{ gap: 14 }}>
            <span>رتبه <b>{faNum(d.me.rank)}</b></span>
            <span>امتیاز <b>{faNum(d.me.points)}</b></span>
            {d.me.played > 0 && <span className="muted">{faNum(d.me.played)} بازی · {faNum(d.me.wins)} برد</span>}
          </div>
        </div>
      )}

      <div className="plate stack">
        <b>جدول</b>
        {d.board.length === 0
          ? <div className="muted">هنوز کسی در این جدول نیست. اولین نفر باش.</div>
          : d.board.map((r) => (
            <div key={r.rank} className={"row cupline" + (r.me ? " me" : "")} style={{ gap: 10 }}>
              <span className="rank">{faNum(r.rank)}</span>
              <span style={{ flex: 1 }}>{r.name || "بی‌نام"}{r.eliminated && <span className="muted"> · حذف</span>}</span>
              {r.played > 0 && <span className="muted">{faNum(r.wins)} برد</span>}
              <b>{faNum(r.points)}</b>
            </div>
          ))}
      </div>
    </div>
  );
}
